import React, { useEffect, useState } from "react";
import NavBar from "../MyComponents/NavBar";
import InteractiveCalendar from "../MyComponents/InteractiveCalendar";
import "../MyComponents/reports.css";
import CocoMetricsDashboard from "../MyComponents/CocoMetricsDashboard";
import { useHistory } from "react-router-dom";

const API_HOST = process.env.REACT_APP_COCO_API_URL;
const API_BASE = `${API_HOST}/filters`;

const COLORS = {
  primary: "#1CA069",
  primaryDark: "#178a5a",
  bg: "#F4F9F8",
  text: "#3D4A52",
  dark: "#072D4B",
  border: "#dfe8e6",
  white: "#ffffff",
};

const START_DATE_MIN = "2019-01-01";
const DEFAULT_END_DATE = "2022-01-01";
const TODAY = new Date().toISOString().split("T")[0];

// Helper to convert Key-Value dictionary object into array of { id, name } objects
const formatOptions = (dictObj) => {
  if (!dictObj || typeof dictObj !== "object") return [];
  return Object.entries(dictObj).map(([id, name]) => ({
    id: String(id),
    name: String(name),
  }));
};

function CocoAnalytics() {
  const initialToken = localStorage.getItem("token");
  const history = useHistory();

  useEffect(() => {
    if (!initialToken) {
      history.push("/login");
    }
  }, [initialToken, history]);

  // Form Filter States - Storing IDs (Default India ID = "1")
  const [country, setCountry] = useState("1");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [block, setBlock] = useState("");
  const [village, setVillage] = useState("");

  const [startDate, setStartDate] = useState(START_DATE_MIN);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);

  // Initialized with default filters (India ID = "1")
  const [appliedFilters, setAppliedFilters] = useState({
    country: "1",
    state: "",
    district: "",
    block: "",
    village: "",
    startDate: START_DATE_MIN,
    endDate: DEFAULT_END_DATE,
    _timestamp: Date.now(),
  });

  // Filter Dropdown Options: Array of { id, name }
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  async function apiGet(path, params) {
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${API_BASE}${path}${qs ? `?${qs}` : ""}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!res.ok) throw new Error("Request failed");
      return await res.json();
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  // Load countries on mount
  useEffect(() => {
    (async () => {
      setLoadingFilters(true);
      const data = await apiGet("/countries", {});
      setCountries(formatOptions(data?.countries));
      setLoadingFilters(false);
    })();
  }, []);

  // Country -> states
  useEffect(() => {
    if (!country) {
      setStates([]);
      setState("");
      setDistricts([]);
      setDistrict("");
      setBlocks([]);
      setBlock("");
      setVillages([]);
      setVillage("");
      return;
    }
    (async () => {
      setLoadingFilters(true);
      const data = await apiGet("/states", { country }); // passes country ID
      setStates(formatOptions(data?.states));
      setLoadingFilters(false);
    })();
  }, [country]);

  // State -> districts
  useEffect(() => {
    if (!state) {
      setDistricts([]);
      setDistrict("");
      setBlocks([]);
      setBlock("");
      setVillages([]);
      setVillage("");
      return;
    }
    (async () => {
      setLoadingFilters(true);
      const data = await apiGet("/districts", { state }); // passes state ID
      setDistricts(formatOptions(data?.districts));
      setDistrict("");
      setBlocks([]);
      setBlock("");
      setVillages([]);
      setVillage("");
      setLoadingFilters(false);
    })();
  }, [state]);

  // District -> blocks
  useEffect(() => {
    if (!district) {
      setBlocks([]);
      setBlock("");
      setVillages([]);
      setVillage("");
      return;
    }
    (async () => {
      setLoadingFilters(true);
      const data = await apiGet("/blocks", { district }); // passes district ID
      setBlocks(formatOptions(data?.blocks));
      setBlock("");
      setVillages([]);
      setVillage("");
      setLoadingFilters(false);
    })();
  }, [district]);

  // Block -> villages
  useEffect(() => {
    if (!block) {
      setVillages([]);
      setVillage("");
      return;
    }
    (async () => {
      setLoadingFilters(true);
      const data = await apiGet("/villages", { block }); // passes block ID
      setVillages(formatOptions(data?.villages));
      setVillage("");
      setLoadingFilters(false);
    })();
  }, [block]);

  // Helper to get name from ID for chip display
  const getNameById = (options, id) => {
    const item = options.find((o) => o.id === String(id));
    return item ? item.name : id;
  };

  const handleApply = () => {
    setAppliedFilters({
      country,
      state,
      district,
      block,
      village,
      startDate,
      endDate,
      _timestamp: Date.now(),
    });
  };

  const handleReset = () => {
    setCountry("");
    setState("");
    setDistrict("");
    setBlock("");
    setVillage("");
    setStartDate(START_DATE_MIN);
    setEndDate(TODAY);
    setAppliedFilters(null);
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    color: "#7d8a88",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    marginBottom: 6,
    display: "block",
  };

  const inputStyle = (disabled) => ({
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13.5,
    color: COLORS.text,
    background: disabled ? "#f5f7f6" : COLORS.white,
    width: "100%",
    outline: "none",
    fontFamily: "sans-serif",
  });

  const Select = ({
    label,
    value,
    onChange,
    options = [],
    disabled,
    placeholder,
  }) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minWidth: 160,
        flexBasis: 180,
        flex: 1,
      }}
    >
      <label style={labelStyle}>{label}</label>
      <select
        style={inputStyle(disabled)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">{placeholder || "All"}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );

  const activeFilterChips = [
    country && { label: "Country", value: getNameById(countries, country) },
    state && { label: "State", value: getNameById(states, state) },
    district && { label: "District", value: getNameById(districts, district) },
    block && { label: "Block", value: getNameById(blocks, block) },
    village && { label: "Village", value: getNameById(villages, village) },
    startDate &&
      endDate && { label: "Date", value: `${startDate} → ${endDate}` },
  ].filter(Boolean);

  return (
    <>
      <NavBar detailView={true} />
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          fontFamily: "sans-serif",
        }}
      >
        <div style={{ padding: "24px", maxWidth: 1440, margin: "0 auto" }}>
          {/* Filter panel */}
          <div
            style={{
              background: COLORS.white,
              borderRadius: 14,
              border: `1px solid ${COLORS.border}`,
              marginBottom: 20,
              overflow: "visible",
              boxShadow: "0 1px 3px rgba(7,45,75,0.06)",
            }}
          >
            <button
              onClick={() => setShowFilters((s) => !s)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                background: COLORS.primary,
                color: "#fff",
                border: "none",
                borderTopLeftRadius: 13,
                borderTopRightRadius: 13,
                borderBottomLeftRadius: showFilters ? 0 : 13,
                borderBottomRightRadius: showFilters ? 0 : 13,
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "sans-serif",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 4h18M6 12h12M10 20h4" strokeLinecap="round" />
                </svg>
                Filters
                {activeFilterChips.length > 0 && (
                  <span
                    style={{
                      background: "rgba(255,255,255,0.22)",
                      fontSize: 11,
                      padding: "2px 9px",
                      borderRadius: 999,
                    }}
                  >
                    {activeFilterChips.length} active
                  </span>
                )}
              </span>
              <span
                style={{
                  transition: "transform 0.2s",
                  transform: showFilters ? "rotate(180deg)" : "none",
                }}
              >
                ▾
              </span>
            </button>

            {showFilters && (
              <div style={{ padding: 20 }}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 150,
                    }}
                  >
                    <InteractiveCalendar
                      label="Start Date"
                      value={startDate}
                      min={START_DATE_MIN}
                      max={TODAY}
                      onChange={setStartDate}
                      presets={[
                        { label: "All Time (2009)", value: START_DATE_MIN },
                        {
                          label: "Start of Year",
                          value: `${new Date().getFullYear()}-01-01`,
                        },
                      ]}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 150,
                    }}
                  >
                    <InteractiveCalendar
                      label="End Date"
                      value={endDate}
                      min={START_DATE_MIN}
                      max={TODAY}
                      onChange={setEndDate}
                      align="right"
                      presets={[{ label: "Today", value: TODAY }]}
                    />
                  </div>

                  <Select
                    label="Country"
                    value={country}
                    onChange={setCountry}
                    options={countries}
                  />
                  <Select
                    label="State"
                    value={state}
                    onChange={setState}
                    options={states}
                    disabled={!country}
                  />
                  <Select
                    label="District"
                    value={district}
                    onChange={setDistrict}
                    options={districts}
                    disabled={!state}
                  />
                  <Select
                    label="Block"
                    value={block}
                    onChange={setBlock}
                    options={blocks}
                    disabled={!district}
                  />
                  <Select
                    label="Village"
                    value={village}
                    onChange={setVillage}
                    options={villages}
                    disabled={!block}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 12,
                    paddingTop: 14,
                    borderTop: `1px solid ${COLORS.border}`,
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {activeFilterChips.map((chip, i) => (
                      <span
                        key={i}
                        style={{
                          background: "rgba(28,160,105,0.1)",
                          color: COLORS.primaryDark,
                          fontSize: 12,
                          fontWeight: 500,
                          padding: "5px 12px",
                          borderRadius: 999,
                        }}
                      >
                        <span style={{ color: COLORS.primary, marginRight: 4 }}>
                          {chip.label}:
                        </span>
                        {chip.value}
                      </span>
                    ))}
                    {loadingFilters && (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#9aa5a3",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            border: `2px solid ${COLORS.primary}`,
                            borderTopColor: "transparent",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "spin 0.8s linear infinite",
                          }}
                        />
                        Loading filter options...
                      </span>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={handleReset}
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#7d8a88",
                        padding: "9px 16px",
                        borderRadius: 8,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontFamily: "sans-serif",
                      }}
                    >
                      Reset
                    </button>
                    <button
                      onClick={handleApply}
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#fff",
                        background: COLORS.primary,
                        padding: "9px 20px",
                        borderRadius: 8,
                        border: "none",
                        cursor: "pointer",
                        boxShadow: "0 1px 2px rgba(28,160,105,0.3)",
                        fontFamily: "sans-serif",
                      }}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dashboard Area */}
          <div
            style={{
              background: COLORS.white,
              borderRadius: 14,
              border: `1px solid ${COLORS.border}`,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(7,45,75,0.06)",
            }}
          >
            {!appliedFilters ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "80px 0",
                  color: "#9aa5a3",
                }}
              >
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ marginBottom: 12 }}
                >
                  <rect x="3" y="4" width="18" height="14" rx="2" />
                  <path d="M8 20h8M12 18v2" />
                </svg>
                <div style={{ fontSize: 13.5 }}>
                  Select filters and click "Apply Filters" to load the
                  dashboard.
                </div>
              </div>
            ) : (
              <CocoMetricsDashboard filters={appliedFilters} />
            )}
          </div>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );
}

export default CocoAnalytics;